import React, { useCallback, useEffect, useState } from 'react';
import {
  Check, Copy, Loader2, PlugZap, Save, Swords, X,
} from 'lucide-react';
import client from '../lib/api';
import SecretInput from './components/SecretInput';
import { useToast } from '../hooks/use-toast';
import { GAME_LANGUAGES } from '../constants/gameLanguages';

const MASKED_SECRET = '••••••••••••••••';

// Casino turu ve spor kuponu **aynı** cüzdan adresine gelir; ayrım
// bildirimin oyun kimliğindedir. Adres Viper hesabında durur ve istekte
// gönderilmez — "Sağlayıcıya kaydet" onu API ile yazar (IP beyaz listesi
// korunur).
const CALLBACK_PATH = '/api/viper/callback';

const LANGUAGES = ['tr', 'en', 'de', 'ru', 'fr', 'es', 'pt', 'ar', 'az'];

const languageLabel = (code) => (
  GAME_LANGUAGES.find((item) => item.value === code)?.label || code.toUpperCase()
);

// Kimlik, secret ve cüzdan adresi spor kitabı kartıyla **ortaktır** (tek
// `provider_configs` satırı); burada değiştirilen değer orada da geçerlidir.
const hydrate = (config) => ({
  ...config,
  api_secret: config.api_secret_configured ? MASKED_SECRET : '',
});

const ViperCasinoSettingsPanel = ({ onClose, onSaved }) => {
  const [config, setConfig] = useState(null);
  const [callbackBase, setCallbackBase] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [probe, setProbe] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const [configResult, settingsResult] = await Promise.allSettled([
      client.get('/admin/viper/config'),
      client.get('/admin/settings'),
    ]);
    if (configResult.status === 'fulfilled') {
      setConfig(hydrate(configResult.value.data));
    } else {
      setError(
        configResult.reason?.response?.data?.detail
        || 'Viper ayarları alınamadı',
      );
    }
    // Adres tabanı sunucudan gelir; tarayıcının origin'i panelin adresi
    // olabilir (bkz. backend/services/site_address.py).
    if (settingsResult.status === 'fulfilled') {
      const bases = settingsResult.value.data?.address_bases || {};
      setCallbackBase(bases.game || bases.site || '');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (key, value) => {
    setConfig((current) => ({ ...current, [key]: value }));
  };

  const callbackUrl = `${callbackBase}${CALLBACK_PATH}`;

  const copyCallback = async () => {
    try {
      await navigator.clipboard.writeText(callbackUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Kopyalanamadı', description: 'Adresi elle kopyalayın.' });
    }
  };

  const save = async () => {
    const payload = {
      api_url: config.api_url || '',
      api_key: config.api_key || '',
      currency: config.currency || 'TRY',
      language: config.language || 'tr',
      slots_enabled: config.slots_enabled !== false,
      live_casino_enabled: config.live_casino_enabled !== false,
      free_rounds_enabled: !!config.free_rounds_enabled,
      callback_registered: !!config.callback_registered,
    };
    // Maskeyi olduğu gibi geri göndermek kayıtlı secret'ı silerdi.
    if (config.api_secret && config.api_secret !== MASKED_SECRET) {
      payload.api_secret = config.api_secret;
    }
    setBusy('save');
    try {
      const { data } = await client.patch('/admin/viper/config', payload);
      setConfig(hydrate(data));
      toast({ title: 'Viper ayarları kaydedildi' });
      await onSaved?.();
    } catch (requestError) {
      toast({
        title: 'Ayarlar kaydedilemedi',
        description: requestError.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setBusy('');
    }
  };

  const test = async () => {
    setBusy('test');
    try {
      const { data } = await client.post('/admin/viper/test');
      setProbe(data);
      toast({
        title: data.connected ? 'Bağlantı çalışıyor' : 'Bağlantı kurulamadı',
        description: data.connected ? undefined : data.error,
      });
    } catch (requestError) {
      toast({
        title: 'Bağlantı test edilemedi',
        description: requestError.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setBusy('');
    }
  };

  const registerCallback = async () => {
    setBusy('callback');
    try {
      const { data } = await client.post('/admin/viper/callback', {
        callback_url: callbackUrl,
      });
      setConfig(hydrate(data.config));
      toast({ title: 'Cüzdan adresi Viper\'a kaydedildi' });
      await onSaved?.();
    } catch (requestError) {
      toast({
        title: 'Cüzdan adresi kaydedilemedi',
        description: requestError.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return (
      <div className="gc-state is-inline">
        <Loader2 size={20} className="gc-spin" />
        <p>Viper ayarları yükleniyor…</p>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="gc-state">
        <X size={24} color="var(--gc-danger, #fb7185)" />
        <h2>Ayarlar yüklenemedi</h2>
        <p>{error || 'Viper ayarları alınamadı.'}</p>
        <button type="button" onClick={onClose} className="gc-btn">Kapat</button>
      </div>
    );
  }

  const secretSaved = !!config.api_secret_configured;
  const ready = (
    !!String(config.api_url || '').trim()
    && !!String(config.api_key || '').trim()
    && (secretSaved || !!String(config.api_secret || '').trim())
  );
  const account = probe?.account;

  return (
    <section className="gc-panel is-tone" style={{ '--gc-tone': '#84cc16' }}>
      <div className="gc-panel-head">
        <span className="gc-card-icon"><Swords size={19} /></span>
        <div className="gc-panel-title">
          <h2>Viper Casino Ayarları</h2>
          <p>
            Viper GGR’ın casino kataloğu: slot, masa ve canlı casino oyunları.
            Cüzdan seamless çalışır; bakiye sitede kalır. API bilgileri ve
            cüzdan adresi <strong>Viper Sportsbook ile ortaktır</strong> —
            burada yapılan değişiklik spor kitabını da etkiler. Viper yalnızca
            beyaz listedeki sunucu IP’lerinden gelen çağrıyı kabul eder.
          </p>
        </div>
        <div className="gc-badges">
          <span className={`gc-badge ${ready ? 'is-on' : 'is-warn'}`}>
            {ready ? 'Bilgiler tam' : 'Bilgi eksik'}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="gc-btn is-icon is-sm"
            aria-label="Ayarları kapat"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      <div className="gc-panel-body">
        <div className="gc-fields">
          <label className="gc-field is-full">
            <span className="gc-label">
              API URL
              {!String(config.api_url || '').trim() && (
                <span className="gc-label-tag is-warn">zorunlu</span>
              )}
            </span>
            <input
              className="gc-input"
              value={config.api_url || ''}
              onChange={(event) => update('api_url', event.target.value)}
              placeholder="https://….viperapiggr.site"
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <label className="gc-field">
            <span className="gc-label">
              API Key
              {!String(config.api_key || '').trim() && (
                <span className="gc-label-tag is-warn">zorunlu</span>
              )}
            </span>
            <input
              className="gc-input"
              value={config.api_key || ''}
              onChange={(event) => update('api_key', event.target.value.trim())}
              placeholder="vk_…"
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <label className="gc-field">
            <span className="gc-label">
              API Secret
              {secretSaved && <span className="gc-label-tag is-ok">kayıtlı</span>}
              {!secretSaved && <span className="gc-label-tag is-warn">zorunlu</span>}
            </span>
            <SecretInput
              value={config.api_secret || ''}
              onChange={(event) => update('api_secret', event.target.value)}
              onFocus={() => {
                if (config.api_secret === MASKED_SECRET) update('api_secret', '');
              }}
              placeholder={secretSaved ? 'Değiştirmek için yeni değer girin' : 'Panelde bir kez gösterilir'}
            />
          </label>
          <label className="gc-field">
            <span className="gc-label">Para Birimi</span>
            <input
              className="gc-input"
              value={config.currency || 'TRY'}
              onChange={(event) => update('currency', event.target.value.toUpperCase())}
              placeholder="TRY"
            />
          </label>
          <label className="gc-field">
            <span className="gc-label">Oyun Dili (yedek)</span>
            <select
              className="gc-input"
              value={config.language || 'tr'}
              onChange={(event) => update('language', event.target.value)}
            >
              {LANGUAGES.map((code) => (
                <option key={code} value={code}>{languageLabel(code)}</option>
              ))}
            </select>
          </label>
        </div>
        <p className="gc-hint">
          Oyun dili önce genel oyun dili ayarından okunur; bu alan yalnızca o
          boşken kullanılır.
        </p>

        <div className="gc-box">
          <div className="gc-box-head">
            <div>
              <h3>Servisin yayınlanacağı bölümler</h3>
              <p>İşareti kaldırılan bölümde Viper oyunları görünmez.</p>
            </div>
          </div>
          <div className="gc-checks">
            <label className="gc-check is-plain">
              <input
                type="checkbox"
                checked={config.slots_enabled !== false}
                onChange={(event) => update('slots_enabled', event.target.checked)}
              />
              <span className="gc-check-text">Slot oyunları</span>
            </label>
            <label className="gc-check is-plain">
              <input
                type="checkbox"
                checked={config.live_casino_enabled !== false}
                onChange={(event) => update('live_casino_enabled', event.target.checked)}
              />
              <span className="gc-check-text">Canlı casino</span>
            </label>
            <label className="gc-check is-plain">
              <input
                type="checkbox"
                checked={!!config.free_rounds_enabled}
                onChange={(event) => update('free_rounds_enabled', event.target.checked)}
              />
              <span className="gc-check-text">Bahissiz kazanç (serbest tur / Viper hediyesi) kabul edilsin</span>
            </label>
          </div>
          <p className="gc-hint">
            Serbest turda ve Viper panelinden verilen freespin/hediyede bahis
            gelmez, yalnızca kazanç bildirilir. Kapalıyken böyle bir kazanç
            reddedilir ve Viper onu yeniden dener; Viper üzerinden hediye
            dağıtmıyorsanız kapalı bırakın. Viper demo açılışı sunmaz.
          </p>
        </div>

        <div className="gc-box is-tone">
          <div className="gc-box-head">
            <div>
              <h3>Cüzdan adresi (callback_url)</h3>
              <p>
                Casino turları ve spor kuponları bu tek adrese imzalı olarak
                gelir. Adres Viper hesabında durur; tanımlanmadan servis açılamaz.
              </p>
            </div>
          </div>
          <div className="gc-copy">
            <code className="gc-copy-url">{callbackUrl}</code>
            <button type="button" onClick={copyCallback} className="gc-btn is-sm">
              {copied ? <><Check size={12} /> Kopyalandı</> : <><Copy size={12} /> Kopyala</>}
            </button>
            <button
              type="button"
              onClick={registerCallback}
              disabled={busy === 'callback' || !callbackBase}
              className="gc-btn is-sm"
              title="Adresi Viper hesabına yazar; IP beyaz listesi korunur"
            >
              {busy === 'callback'
                ? <Loader2 size={12} className="gc-spin" />
                : <PlugZap size={12} />}
              Sağlayıcıya kaydet
            </button>
          </div>
          <div className="gc-checks">
            <label className="gc-check is-plain">
              <input
                type="checkbox"
                checked={!!config.callback_registered}
                onChange={(event) => update('callback_registered', event.target.checked)}
              />
              <span className="gc-check-text">Adres Viper hesabında tanımlı</span>
            </label>
          </div>
        </div>

        <div className="gc-box">
          <div className="gc-box-head">
            <div>
              <h3>Bağlantı testi</h3>
              <p>
                Viper hesabını okur; işlem üretmez, oyuncu açmaz. Kayıtlı
                bilgilerle çalışır — önce kaydedin.
              </p>
            </div>
            <button
              type="button"
              onClick={test}
              disabled={busy === 'test'}
              className="gc-btn is-sm"
            >
              {busy === 'test' ? <Loader2 size={13} className="gc-spin" /> : <PlugZap size={13} />}
              Bağlantıyı Test Et
            </button>
          </div>
          {probe && (
            <p className={`gc-hint ${probe.connected && account?.can_play ? '' : 'is-warn'}`}>
              {probe.connected
                ? `Bağlantı çalışıyor · hesap ${account?.status || '—'} · bakiye ${account?.balance_usd || '0'} USD`
                  + (account?.can_play
                    ? ''
                    : ' · Viper yeni açılış ve bahis kabul etmiyor (bakiye/kredi tükenmiş ya da hesap askıda)')
                  + (probe.callback_url
                    ? ` · kayıtlı cüzdan adresi: ${probe.callback_url}`
                    : ' · Viper hesabında cüzdan adresi yok')
                : `${probe.error_code ? `${probe.error_code} · ` : ''}${probe.error}`}
            </p>
          )}
        </div>
      </div>

      <div className="gc-panel-foot">
        <span className="gc-foot-note">
          Oyun listesi için kaydettikten sonra karttaki “Oyunları Çek”
          düğmesini kullanın.
        </span>
        <button
          type="button"
          onClick={save}
          disabled={busy === 'save'}
          className="gc-btn is-primary"
        >
          {busy === 'save' ? <Loader2 size={14} className="gc-spin" /> : <Save size={14} />}
          Ayarları Kaydet
        </button>
      </div>
    </section>
  );
};

export default ViperCasinoSettingsPanel;
